<script setup lang="ts">
import type { IProps, IEmits } from './UiButton.types'

const props = withDefaults(defineProps<IProps>(), {
  size: 'big',
  variant: 'primary',
  disabled: false,
})
const emit = defineEmits<IEmits>()
const onHandleClickWithoutTarget = () => {
  if (props.disabled) return

  emit('clicked')
}

</script>

<template>
  <NuxtLink
    v-if="to"
    :class="[
      'button',
      `button--${variant}`,
      `button--${size}`,
      { 'button--disabled': disabled },
    ]"
    :to="to"
    :target="target"
  >
    <slot />
  </NuxtLink>

  <div
    v-else
    :class="[
      'button',
      `button--${variant}`,
      `button--${size}`,
      { 'button--disabled': disabled },
    ]"
    @click.stop="onHandleClickWithoutTarget"
  >
    <slot />
  </div>
</template>

<style lang="scss" scoped>
.button {
  display: flex;
  justify-content: center;
  align-items: center;
  min-width: 12.8rem;
  width: auto;
  width: fit-content;
  padding: 1.4rem 2.4rem;
  font-size: 1.4rem;
  text-align: center;
  background-color: transparent;
  border: 0;
  border-radius: 5rem;
  cursor: pointer;
  $root: &;

  &--primary {
    color: $white;
    background-color: $text-400;
  }

  &--small {
    height: 4.6rem;
    font-size: 1.5rem;
  }

  &--big {
    height: 4.8rem;
  }

  &.button--tertiary {
    @include body-3-medium;
    min-width: 0;
    height: fit-content;
    padding: 0;
    color: $text-400;
    background-color: transparent;
  }

  &--ghost {
    color: $darkblue-400;
    font-weight: 500;
    font-size: 1.8rem;
    line-height: 2.4rem;
  }

  &--secondary {
    border: 1px solid $gray-500;
  }

  &--bright {
    color: $text-400;
    background-color: $white;
  }

  &.button--disabled {
    color: $gray-400;
    cursor: default;
    pointer-events: none;
  }

  @include atM {
    min-width: 14rem;
    padding: 1.6rem 3.2rem;
    font-size: 1.6rem;

    &--big {
      height: 5.6rem;
    }
  }
}
</style>
